import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import api from "../lib/api";
import { toast } from "react-hot-toast";

export type NotificationType =
  | "ticket_created"
  | "ticket_assigned"
  | "ticket_shared"
  | "status_changed"
  | "comment_added"
  | "priority_changed"
  | "workshop_available";

export interface Notification {
  id: string;
  // string: el seed viejo dejó tipos que el código ya no usa.
  type: NotificationType | (string & {});
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  ticketId?: string | null;
  metadata?: Record<string, any> | null;
  /** Adónde lleva (lo arma el back al leer); null si no lleva a ningún lado. */
  link?: string | null;
  /** Número real del ticket, no el id. */
  ticketNumber?: number | null;
  /** El ticket ya no existe (lo borraron). */
  targetMissing?: boolean;
}

/** Lo que devuelve /api/notifications/user/summary. */
export interface NotificationsSummary {
  /** Todas las sin leer del usuario, no solo las de la lista. */
  unreadCount: number;
  /** La más nueva; null si no tiene ninguna. */
  latestId: string | null;
}

/** Qué se abrió: el back ya marcó sus notificaciones como leídas. */
export interface OpenedTarget {
  ticketId?: string;
  resourceId?: string;
  resourceSlug?: string;
}

// Refresco en segundo plano: cada minuto con la pestaña visible y al volver
// a ella, sin repetir si se pidió hace poco. Lo que se chequea es el resumen
// (no leídas + la más nueva, unos bytes); la lista de 50 se pide solo al
// mostrarla (campanita abierta o página de Notificaciones) y, mientras se
// muestra, cuando el resumen cambia.
const POLL_MS = 60_000;
const MIN_REFETCH_MS = 15_000;

export interface NotificationPreferences {
  email: boolean;
  inApp: boolean;
  ticketAssigned: boolean;
  statusChanged: boolean;
  commentAdded: boolean;
  priorityChanged: boolean;
}

interface NotificationsContextType {
  notifications: Notification[];
  preferences: NotificationPreferences | null;
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markOpenedAsReadLocally: (target: OpenedTarget) => void;
  updatePreferences: (
    newPreferences: Partial<NotificationPreferences>,
  ) => Promise<void>;
  testEmailConnection: () => Promise<boolean>;
  sendTestEmail: (
    to: string,
    subject: string,
    message: string,
  ) => Promise<boolean>;
  refreshNotifications: () => Promise<void>;
  /** Avisa que se empieza a mostrar la lista; devuelve cómo dejar de mostrarla. */
  watchList: () => () => void;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

const noop = () => {};

/**
 * Para las páginas que pueden renderizarse fuera del provider (tests): sin
 * provider no hace nada.
 */
export const useMarkOpenedAsReadLocally = (): ((
  target: OpenedTarget,
) => void) => {
  const context = useContext(NotificationsContext);
  return context?.markOpenedAsReadLocally ?? noop;
};

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotificationsContext must be used within a NotificationsProvider",
    );
  }
  return context;
};

/**
 * Lo usa quien muestra la lista (la campanita abierta, la página de
 * Notificaciones): la pide si nunca se pidió o si cambió algo desde entonces,
 * y la mantiene al día mientras está `active`.
 */
export const useNotificationsList = (active = true) => {
  const { watchList } = useNotificationsContext();
  useEffect(() => (active ? watchList() : undefined), [active, watchList]);
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [preferences, setPreferences] =
    useState<NotificationPreferences | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const lastCheckRef = useRef(0);
  // Copia síncrona de la lista, para saber qué estaba sin leer al marcar.
  const notificationsRef = useRef<Notification[]>([]);
  // Lo que se sabe del back, con lo marcado acá ya descontado: si el resumen
  // trae otra cosa, algo cambió. undefined hasta el primer resumen.
  const unreadRef = useRef(0);
  const latestIdRef = useRef<string | null | undefined>(undefined);
  // Cuántos muestran la lista y el pedido en curso (para no pedirla dos
  // veces a la vez).
  const listViewersRef = useRef(0);
  const listRequestRef = useRef<Promise<unknown> | null>(null);
  // Pedidos y marcas se numeran en orden (seqRef), para saber qué es más
  // nuevo que qué: la lista cargada (0 si no hay), el primer resumen, el
  // último aplicado, el último que vio un cambio y la última marca de leída
  // hecha acá.
  const seqRef = useRef(0);
  const listSeqRef = useRef(0);
  const firstSummarySeqRef = useRef(0);
  const summarySeqRef = useRef(0);
  const changeSeqRef = useRef(0);
  const markSeqRef = useRef(0);
  // Marcas cuyo PATCH todavía no volvió.
  const pendingMarksRef = useRef(0);

  const updateList = useCallback(
    (update: (prev: Notification[]) => Notification[]) => {
      notificationsRef.current = update(notificationsRef.current);
      setNotifications(notificationsRef.current);
    },
    [],
  );

  const applyUnread = useCallback((count: number) => {
    unreadRef.current = Math.max(0, count);
    setUnreadCount(unreadRef.current);
  }, []);

  // Si la lista hay que pedirla: nunca llegó, o puede faltarle algo que vio
  // el resumen. Si coincide con él (la más nueva y las sin leer) está al día.
  // Si no, está atrasada si se pidió antes de un resumen que vio un cambio.
  // Si se pidió antes del primer resumen (por ejemplo, porque fallaron los
  // anteriores) no hay cambio con qué comparar: está atrasada si su más
  // nueva es otra o muestra más sin leer de las que hay (con más de 50, las
  // sin leer viejas no vienen en la lista y las cuenta solo el resumen).
  const listNeedsFetch = useCallback(() => {
    if (!listSeqRef.current) return true;
    if (latestIdRef.current === undefined) return false;
    const list = notificationsRef.current;
    const newest = list[0]?.id ?? null;
    const unread = list.filter((n) => !n.read).length;
    if (newest === latestIdRef.current && unread === unreadRef.current) {
      return false;
    }
    if (changeSeqRef.current > listSeqRef.current) return true;
    return (
      firstSummarySeqRef.current > listSeqRef.current &&
      (newest !== latestIdRef.current || unread > unreadRef.current)
    );
  }, []);

  // Fetch user notifications. `silent`: el refresco en segundo plano no
  // muestra el spinner de la lista.
  const fetchNotifications = useCallback(
    async (silent = false): Promise<void> => {
      if (!silent) setIsLoading(true);
      const seq = ++seqRef.current;
      const request = api.get("/api/notifications/user");
      listRequestRef.current = request;
      let loaded = false;
      try {
        const response = await request;
        // Si ya llegó una pedida después, esta es más vieja.
        if (response.data.success && seq > listSeqRef.current) {
          updateList(() => response.data.data);
          listSeqRef.current = seq;
          loaded = true;
        }
      } catch (error: any) {
        console.error("Error fetching notifications:", error);
      } finally {
        if (listRequestRef.current === request) listRequestRef.current = null;
        if (!silent) setIsLoading(false);
      }
      // Mientras llegaba, un resumen pudo ver algo que no trae: si se está
      // mostrando, se pide de nuevo. Si falló, la reintenta el próximo
      // resumen.
      if (
        loaded &&
        listViewersRef.current > 0 &&
        !listRequestRef.current &&
        listNeedsFetch()
      ) {
        void fetchNotifications(true);
      }
    },
    [updateList, listNeedsFetch],
  );

  // Chequeo liviano. Si cambió algo y alguien está mirando la lista, se trae
  // de nuevo (o al llegar la que está en camino, si no lo trae); si no, se
  // trae al volver a mostrarla.
  const checkSummary = useCallback(async () => {
    lastCheckRef.current = Date.now();
    const seq = ++seqRef.current;
    const marking = pendingMarksRef.current > 0;
    try {
      const response = await api.get("/api/notifications/user/summary");
      if (!response.data.success) return;
      // Se descarta si ya llegó uno más nuevo, o si se marcó algo como leído
      // mientras tanto: el back pudo armarlo antes de guardar la marca y
      // volvería a subir el contador. Lo que haya lo trae el próximo chequeo.
      if (seq < summarySeqRef.current || marking || markSeqRef.current > seq) {
        return;
      }
      summarySeqRef.current = seq;
      const summary: NotificationsSummary = response.data.data;
      if (latestIdRef.current === undefined) {
        firstSummarySeqRef.current = seq;
      } else if (
        summary.unreadCount !== unreadRef.current ||
        summary.latestId !== latestIdRef.current
      ) {
        changeSeqRef.current = seq;
      }
      latestIdRef.current = summary.latestId;
      applyUnread(summary.unreadCount);
      if (
        listViewersRef.current > 0 &&
        !listRequestRef.current &&
        listNeedsFetch()
      ) {
        void fetchNotifications(true);
      }
    } catch (error: any) {
      console.error("Error fetching notifications summary:", error);
    }
  }, [applyUnread, fetchNotifications, listNeedsFetch]);

  const watchList = useCallback(() => {
    listViewersRef.current += 1;
    if (!listRequestRef.current && listNeedsFetch()) {
      // Con algo ya cargado se muestra eso mientras llega lo nuevo.
      void fetchNotifications(listSeqRef.current > 0);
    }
    return () => {
      listViewersRef.current -= 1;
    };
  }, [fetchNotifications, listNeedsFetch]);

  // Fetch user notification preferences
  const fetchPreferences = useCallback(async () => {
    try {
      const response = await api.get("/api/notifications/preferences");
      if (response.data.success) {
        setPreferences(response.data.data);
      }
    } catch (error: any) {
      console.error("Error fetching preferences:", error);
    }
  }, []);

  // Mark notification as read. Optimista: se marca en pantalla primero y,
  // si el back falla, vuelve a quedar sin leer.
  const markAsRead = useCallback(
    async (notificationId: string) => {
      const wasUnread = notificationsRef.current.some(
        (n) => n.id === notificationId && !n.read,
      );
      const setRead = (read: boolean) => {
        updateList((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read } : n)),
        );
        if (wasUnread) applyUnread(unreadRef.current + (read ? -1 : 1));
      };
      setRead(true);
      markSeqRef.current = ++seqRef.current;
      pendingMarksRef.current += 1;
      try {
        const response = await api.patch(
          `/api/notifications/${notificationId}/read`,
        );
        if (!response.data.success) setRead(false);
      } catch (error: any) {
        setRead(false);
        console.error("Error marking notification as read:", error);
      } finally {
        pendingMarksRef.current -= 1;
      }
    },
    [updateList, applyUnread],
  );

  // Al abrir un ticket o un recurso, el back ya marca sus notificaciones;
  // acá solo se refleja en pantalla, sin otro pedido. Sin la lista cargada
  // (o con una vieja) no se sabe cuántas eran: se pregunta el resumen, que
  // es liviano.
  const markOpenedAsReadLocally = useCallback(
    (target: OpenedTarget) => {
      const matches = (n: Notification) => {
        if (target.ticketId && n.ticketId === target.ticketId) return true;
        if (n.type !== "workshop_available" || !n.metadata) return false;
        return (
          (!!target.resourceSlug &&
            n.metadata.resourceSlug === target.resourceSlug) ||
          (!!target.resourceId && n.metadata.resourceId === target.resourceId)
        );
      };
      const opened = notificationsRef.current.filter(
        (n) => !n.read && matches(n),
      ).length;
      if (opened > 0) {
        markSeqRef.current = ++seqRef.current;
        updateList((prev) =>
          prev.map((n) => (!n.read && matches(n) ? { ...n, read: true } : n)),
        );
        applyUnread(unreadRef.current - opened);
      } else if (listNeedsFetch() && unreadRef.current > 0) {
        void checkSummary();
      }
    },
    [updateList, applyUnread, checkSummary, listNeedsFetch],
  );

  // Mark all notifications as read
  const markAllAsRead = async () => {
    markSeqRef.current = ++seqRef.current;
    pendingMarksRef.current += 1;
    try {
      const response = await api.patch("/api/notifications/mark-all-read");
      if (response.data.success) {
        updateList((prev) => prev.map((n) => ({ ...n, read: true })));
        applyUnread(0);
      }
    } catch (error: any) {
      console.error("Error marking all notifications as read:", error);
    } finally {
      pendingMarksRef.current -= 1;
    }
  };

  // Update notification preferences
  const updatePreferences = async (
    newPreferences: Partial<NotificationPreferences>,
  ) => {
    try {
      const response = await api.patch(
        "/api/notifications/preferences",
        newPreferences,
      );
      if (response.data.success) {
        setPreferences((prev) => {
          if (!prev) return newPreferences as NotificationPreferences;
          return { ...prev, ...newPreferences } as NotificationPreferences;
        });
        toast.success("Preferencias actualizadas correctamente");
      }
    } catch (error: any) {
      toast.error("Error al actualizar preferencias");
      console.error("Error updating preferences:", error);
    }
  };

  // Test email connection (admin only)
  const testEmailConnection = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/notifications/test-connection");
      if (response.data.success) {
        toast.success("✅ Conexión de email verificada correctamente");
        return true;
      }
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message ||
        "Error al verificar conexión de email";
      toast.error(`❌ ${message}`);
      return false;
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  // Send test email (admin only)
  const sendTestEmail = async (
    to: string,
    subject: string,
    message: string,
  ) => {
    setIsLoading(true);
    try {
      const response = await api.post("/api/notifications/test-email", {
        to,
        subject,
        message,
      });

      if (response.data.success) {
        toast.success("✅ Email de prueba enviado correctamente");
        return true;
      }
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message ||
        "Error al enviar email de prueba";
      toast.error(`❌ ${message}`);
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  // Botón "Actualizar": la lista y el contador. Si el resumen ve algo que la
  // lista no trae, se pide otra vez al llegar.
  const refreshNotifications = useCallback(async () => {
    await Promise.all([fetchNotifications(), checkSummary()]);
  }, [fetchNotifications, checkSummary]);

  // Al montar, solo el contador: la lista espera a que alguien la muestre.
  useEffect(() => {
    void checkSummary();
    fetchPreferences();
  }, [checkSummary, fetchPreferences]);

  // Sin esto, el contador no mostraba las nuevas hasta recargar la página.
  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastCheckRef.current < MIN_REFETCH_MS) return;
      void checkSummary();
    };
    const timer = window.setInterval(refreshIfVisible, POLL_MS);
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [checkSummary]);

  const value: NotificationsContextType = {
    notifications,
    preferences,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    markOpenedAsReadLocally,
    updatePreferences,
    testEmailConnection,
    sendTestEmail,
    refreshNotifications,
    watchList,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
